import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Trash2, AlertTriangle, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import BottomNav from "@/components/BottomNav";

const AccountDeletionPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState("");
  const [reason, setReason] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const reasons = [
    { id: "found_someone", label: "I found someone special" },
    { id: "taking_break", label: "Taking a break from dating" },
    { id: "not_useful", label: "App is not useful for me" },
    { id: "privacy", label: "Privacy concerns" },
    { id: "other", label: "Other reason" },
  ];

  const handleDeleteAccount = async () => {
    if (confirmText.toLowerCase() !== "delete") {
      toast.error("Please type DELETE to confirm");
      return;
    }

    setIsDeleting(true);
    try {
      await api.delete("/account", { data: { reason } });
      toast.success("Account deleted successfully");
      logout();
      navigate("/");
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error(error.response?.data?.detail || "Failed to delete account. Please try again.");
      setIsDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b px-4 py-4 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1">
          <ArrowLeft className="w-6 h-6 text-gray-700" />
        </button>
        <h1 className="text-lg font-bold text-red-600">Delete Account</h1>
      </div>

      {/* Progress */}
      <div className="flex justify-center gap-2 py-4">
        {[1, 2, 3].map((s) => (
          <div
            key={s}
            className={`w-3 h-3 rounded-full ${s <= step ? "bg-red-500" : "bg-gray-300"}`}
          />
        ))}
      </div>

      <div className="px-4">
        {/* Step 1: Warning */}
        {step === 1 && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-center text-gray-900 mb-2">
                Delete Your Account?
              </h2>
              <p className="text-gray-600 text-center text-sm mb-6">
                This action is permanent and cannot be undone.
              </p>

              <div className="bg-red-50 rounded-xl p-4 space-y-2">
                <p className="text-sm text-red-800 font-medium">You will lose:</p>
                <ul className="text-sm text-red-700 space-y-1">
                  <li>• Your profile and all photos</li>
                  <li>• All your matches and conversations</li>
                  <li>• Your recovery milestones</li>
                  <li>• Any remaining coins or subscription time</li>
                </ul>
              </div>
            </div>

            <button
              onClick={() => setStep(2)}
              className="w-full py-4 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition"
              data-testid="continue-deletion-btn"
            >
              Continue with Deletion
            </button>

            <button
              onClick={() => navigate(-1)}
              className="w-full py-4 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition"
            >
              Keep My Account
            </button>
          </div>
        )}

        {/* Step 2: Reason */}
        {step === 2 && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                Help Us Improve
              </h2>
              <p className="text-gray-600 text-sm mb-6">
                Why are you leaving? (Optional)
              </p>

              <div className="space-y-3">
                {reasons.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => setReason(r.id)}
                    className={`w-full flex items-center gap-3 p-4 rounded-xl border-2 transition ${
                      reason === r.id
                        ? "border-red-500 bg-red-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    data-testid={`reason-${r.id}`}
                  >
                    <div
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        reason === r.id ? "border-red-500" : "border-gray-300"
                      }`}
                    >
                      {reason === r.id && (
                        <div className="w-3 h-3 rounded-full bg-red-500" />
                      )}
                    </div>
                    <span className={reason === r.id ? "text-red-700" : "text-gray-700"}>
                      {r.label}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <button
              onClick={() => setStep(3)}
              className="w-full py-4 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition"
            >
              Continue
            </button>

            <button
              onClick={() => setStep(1)}
              className="w-full py-4 text-gray-500 font-medium"
            >
              Go Back
            </button>
          </div>
        )}

        {/* Step 3: Confirm */}
        {step === 3 && (
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-6 shadow-sm">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-center text-gray-900 mb-2">
                Final Confirmation
              </h2>
              <p className="text-gray-600 text-center text-sm mb-6">
                Type <span className="font-bold text-red-600">DELETE</span> to permanently delete your account
              </p>

              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="Type DELETE"
                className="w-full px-4 py-4 text-center text-lg font-bold border-2 border-red-300 rounded-xl focus:border-red-500 focus:ring-2 focus:ring-red-200 outline-none"
                data-testid="delete-confirm-input"
              />

              <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                <p className="text-xs text-gray-500 text-center">
                  Account: {user?.email}
                </p>
              </div>
            </div>

            <button
              onClick={handleDeleteAccount}
              disabled={isDeleting || confirmText.toLowerCase() !== "delete"}
              className="w-full py-4 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="confirm-delete-btn"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="w-5 h-5" />
                  Delete My Account Forever
                </>
              )}
            </button>

            <button
              onClick={() => setStep(2)}
              disabled={isDeleting}
              className="w-full py-4 text-gray-500 font-medium"
            >
              Go Back
            </button>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default AccountDeletionPage;
